import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { PageHead } from "@/components/PageHead";
import { Seo } from "@/components/Seo";
import { FACEBOOK_POSTS, type FacebookPost } from "@/content/facebookPosts";
import type { LanguageBinding } from "@/hooks/useLanguage";
import { cx } from "@/lib/classnames";
import { formatLongDate } from "@/lib/dates";
import { isOverrideExcluded, mergePost } from "@/lib/postOverrides";

const BATCH = 24;

function excerpt(text: string, max = 120): string {
  const clean = text.replace(/\s+/g, " ").trim();
  if (clean.length <= max) return clean;
  return clean.slice(0, max).trimEnd() + "…";
}

const PostCard: React.FC<{
  post: FacebookPost;
  language: LanguageBinding;
  index: number;
}> = ({ post, language, index }) => {
  const { t, lang, isRTL } = language;
  const base = import.meta.env.BASE_URL;
  const thumb = post.photos[0]?.thumb;
  const titleForLang = lang === "ar" ? post.titleAr : post.titleEn;
  const bodyForLang = lang === "ar" ? post.textAr : post.textEn;
  const displayHeadline = titleForLang || bodyForLang; // title if present, else body excerpt
  return (
    <Link
      to={`/posts/${post.slug}`}
      className="group block"
      style={{
        border: "1px solid var(--hairline-dark)",
        background: "var(--card-surface)",
      }}
    >
      {thumb ? (
        <div className="relative aspect-[4/3] overflow-hidden">
          <img
            src={`${base}${thumb.replace(/^\//, "")}`}
            alt=""
            loading="lazy"
            decoding="async"
            className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-[1.03]"
          />
          {post.photos.length > 1 && (
            <div
              className={cx(
                isRTL ? "ff-ar-sans" : "ff-en-sc tracked-wide",
                "absolute bottom-2 end-2 px-2 py-1 text-[10px]",
              )}
              style={{
                background: "rgba(11,9,8,.75)",
                color: "var(--parchment)",
                backdropFilter: "blur(2px)",
              }}
            >
              {t.postsPage.photoCount(post.photos.length)}
            </div>
          )}
        </div>
      ) : (
        // Text-only post — no photo attachment in the FB export. Render a
        // compact quote-style banner so the card still reads as intentional,
        // not a missing-image placeholder.
        <div
          className="relative aspect-[4/3] flex items-center justify-center p-6 text-center overflow-hidden"
          style={{
            background:
              "linear-gradient(160deg, rgba(200,68,30,.12) 0%, rgba(11,9,8,.4) 60%, rgba(11,9,8,.7) 100%)",
          }}
        >
          <svg
            width="44"
            height="44"
            viewBox="0 0 44 44"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.2"
            aria-hidden
            style={{ color: "var(--gold-2)", opacity: 0.7 }}
          >
            <path d="M10 14 Q10 10 14 10 L30 10 Q34 10 34 14 L34 28 Q34 32 30 32 L14 32 Q10 32 10 28 Z" />
            <path d="M14 17 L30 17 M14 22 L30 22 M14 27 L24 27" />
          </svg>
        </div>
      )}
      <div className="p-5">
        <div className="flex items-baseline gap-3 mb-2">
          <span className="cat-num text-[12px]" style={{ color: "var(--vermillion-2)" }}>
            {String(index + 1).padStart(2, "0")}
          </span>
          <span
            className={cx(isRTL ? "ff-ar-sans" : "ff-en-sc tracked-wide", "text-[10px]")}
            style={{ color: "var(--muted-dark)" }}
          >
            {formatLongDate(post.dateIso, lang)}
          </span>
        </div>
        <p
          dir={lang === "ar" ? "rtl" : "ltr"}
          lang={lang}
          className={cx(
            lang === "ar" ? "ff-ar-serif" : "ff-en-serif",
            "text-base leading-[1.6]",
          )}
          style={{ color: "var(--parchment)" }}
        >
          {excerpt(displayHeadline)}
        </p>
        <div
          className={cx(
            isRTL ? "ff-ar-sans" : "ff-en-sc tracked-wide",
            "mt-4 text-[11px]",
          )}
          style={{ color: "var(--vermillion-2)" }}
        >
          {t.postsPage.readMore} →
        </div>
      </div>
    </Link>
  );
};

export const Posts: React.FC<{ language: LanguageBinding }> = ({ language }) => {
  const { t, lang, isRTL } = language;
  const sorted = useMemo(() => {
    // Filter to posts that (a) are not soft-excluded via postOverrides and
    // (b) have content in the active UI language. Sort newest-first. Any
    // per-post editorial overrides (title/body) are merged at this stage so
    // downstream render code sees the final display values.
    const arr = FACEBOOK_POSTS.filter((p) => !isOverrideExcluded(p.slug))
      .map(mergePost)
      .filter((p) =>
        lang === "ar" ? p.textAr.length > 0 : p.textEn.length > 0,
      );
    // Newest-first. Parse the ISO timestamp explicitly so same-day posts
    // tie-break on the exact minute Facebook recorded, not on slug order.
    arr.sort((a, b) => {
      const ta = a.dateIso ? Date.parse(a.dateIso) : 0;
      const tb = b.dateIso ? Date.parse(b.dateIso) : 0;
      if (tb !== ta) return tb - ta;
      // Final tiebreaker for identical timestamps: source-file position
      return b.sourceEntryIndex - a.sourceEntryIndex;
    });
    return arr;
  }, [lang]);
  const [visible, setVisible] = useState(Math.min(BATCH, sorted.length));
  const sentinelRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (visible >= sorted.length) return;
    const el = sentinelRef.current;
    if (!el) return;
    const io = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            setVisible((v) => Math.min(v + BATCH, sorted.length));
          }
        }
      },
      { rootMargin: "400px 0px" },
    );
    io.observe(el);
    return () => io.disconnect();
  }, [visible, sorted.length]);

  return (
    <>
      <Seo
        title={t.meta.posts.title}
        description={t.meta.posts.description}
        lang={lang}
        path="/posts"
      />
      <PageHead
        kicker={t.postsPage.kicker}
        title={t.postsPage.title}
        tagline={t.postsPage.intro}
        isRTL={isRTL}
      />

      <section className="relative" style={{ background: "var(--ink-2)" }}>
        <div className="grain" />
        <div className="max-w-page mx-auto px-6 md:px-10 py-20 md:py-24 relative">
          {sorted.length === 0 ? (
            <p
              className={cx(isRTL ? "ff-ar-sans" : "ff-en-body italic")}
              style={{ color: "var(--muted-dark)" }}
            >
              {t.postsPage.empty}
            </p>
          ) : (
            <>
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                {sorted.slice(0, visible).map((post, i) => (
                  <PostCard key={post.slug} post={post} index={i} language={language} />
                ))}
              </div>
              {visible < sorted.length && (
                <div ref={sentinelRef} className="h-16 mt-12 flex items-center justify-center">
                  <button
                    onClick={() => setVisible((v) => Math.min(v + BATCH, sorted.length))}
                    className={cx(
                      isRTL ? "ff-ar-sans" : "ff-en-sc tracked-wide",
                      "text-[12px] px-6 py-3 rounded-full",
                    )}
                    style={{
                      color: "var(--parchment)",
                      border: "1px solid var(--hairline-dark)",
                    }}
                  >
                    {t.postsPage.loadMore}
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      </section>
    </>
  );
};
